<script setup>
import { computed } from 'vue';
import SalesCommissionCompanySection from './SalesCommissionCompanySection.vue';
import SalesCommissionInstallmentsSection from './SalesCommissionInstallmentsSection.vue';
import SalesCommissionParticipantsSection from './SalesCommissionParticipantsSection.vue';
import { GROUP_CATALOG_KEYS } from '../composables/salesCommissionParticipantForm';
import { sourceOptionistOptions } from '../composables/salesCommissionForm';

const props = defineProps({
  active: { type: String, required: true },
  catalog: { type: Object, default: () => ({}) },
  errors: { type: Object, default: () => ({}) },
  form: { type: Object, required: true },
  payerOptions: { type: Array, default: () => [] },
  sections: { type: Array, default: () => [] },
});

const emit = defineEmits([
  'changeCount',
  'distributePercentages',
  'setCount',
  'toggleGroup',
  'update',
  'updateCompany',
  'updateGroup',
  'updateOptionistReport',
  'updateParticipant',
  'updatePayer',
  'updatePayerCount',
]);

const isParticipantSection = computed(() =>
  props.sections.slice(2).includes(props.active)
);
const participantGroup = computed(
  () => props.form.participants?.[props.active] || { rows: [] }
);
const participantOptions = computed(() => {
  const catalogOptions = props.catalog[GROUP_CATALOG_KEYS[props.active]] || [];
  const options =
    props.active === 'optionist_brokers'
      ? [
          ...catalogOptions,
          ...sourceOptionistOptions(props.form.source_optionists),
        ]
      : catalogOptions;
  return options.filter(
    (option, index) =>
      options.findIndex(item => String(item.id) === String(option.id)) === index
  );
});
</script>

<template>
  <SalesCommissionCompanySection
    v-if="active === 'company'"
    :form="form"
    :errors="errors"
    @update="(field, value) => emit('updateCompany', field, value)"
  />
  <SalesCommissionInstallmentsSection
    v-else-if="active === 'installments'"
    :form="form"
    :errors="errors"
    :payer-options="payerOptions"
    @change-count="count => emit('changeCount', count)"
    @update="(index, field, value) => emit('update', index, field, value)"
    @update-payer="
      (installment, payer, field, value) =>
        emit('updatePayer', installment, payer, field, value)
    "
    @update-payer-count="
      (installment, count) => emit('updatePayerCount', installment, count)
    "
    @update-notes="value => emit('updateCompany', 'notes', value)"
  />
  <SalesCommissionParticipantsSection
    v-else-if="isParticipantSection"
    :group="active"
    :value="participantGroup"
    :errors="errors"
    :options="participantOptions"
    :banks="catalog.bancos || []"
    :pix-keys="catalog.chaves_pix || []"
    :report-types="catalog.tipo_laudo_opcionista || []"
    @distribute-percentages="group => emit('distributePercentages', group)"
    @set-count="(group, count) => emit('setCount', group, count)"
    @toggle-group="(group, enabled) => emit('toggleGroup', group, enabled)"
    @update-group="
      (group, field, value) => emit('updateGroup', group, field, value)
    "
    @update-optionist-report="value => emit('updateOptionistReport', value)"
    @update-participant="
      (group, index, field, value) =>
        emit('updateParticipant', group, index, field, value)
    "
  />
</template>
